import { useEffect, useState } from "react";
import { authHeaders } from "./auth";

/** Riga di GET /api/calls/history (solo le chiamate dell'utente collegato). */
interface HistoryItem {
  id: string;
  direction: "outgoing" | "incoming";
  other: { id: string; name: string; department: string };
  createdAt: string;
  answeredAt: string | null;
  endedAt: string | null;
  durationSeconds: number;
  endReason: string | null;
  documents: number;
  recordings: number;
  recordingFiles: { id: string; fileName: string; sizeBytes: number; durationSeconds: number | null }[];
}
interface HistoryPage { total: number; page: number; size: number; items: HistoryItem[] }

const PAGE = 20;

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit" }) + " " + d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
}
function fmtDuration(s: number): string {
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}
function outcome(i: HistoryItem): string {
  if (!i.endedAt) return "in corso";
  if (!i.answeredAt) return i.endReason === "rejected" ? "rifiutata" : i.endReason === "timeout" ? "senza risposta" : "annullata";
  return i.endReason === "interrupted" ? "interrotta" : "conclusa";
}

/** Registro delle chiamate dell'esperto collegato (21/10/2026): pagina a sé, con paginazione. */
export default function CallHistory({ token, onBack }: { token: string; onBack: () => void }) {
  const [page, setPage] = useState(0);
  const [data, setData] = useState<HistoryPage | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setError(null);
    fetch(`/api/calls/history?page=${page}&size=${PAGE}`, { headers: authHeaders(token) })
      .then(async (r) => { if (!r.ok) throw new Error(`Il server ha risposto ${r.status}`); return (await r.json()) as HistoryPage; })
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [page, token]);

  const pages = data ? Math.max(1, Math.ceil(data.total / PAGE)) : 1;

  return (
    <section className="card history">
      <h2>Le mie chiamate <button className="ghost" onClick={onBack}>← Indietro</button></h2>
      {error && <p className="login-error">{error}</p>}
      {data && data.items.length === 0 && <p className="muted">Nessuna chiamata registrata.</p>}
      {data && data.items.length > 0 && (
        <table className="history-table">
          <thead>
            <tr><th>Inizio</th><th>Con</th><th>Direzione</th><th>Durata</th><th>Esito</th><th>Documenti</th><th>Registrazione</th></tr>
          </thead>
          <tbody>
            {data.items.map((i) => (
              <tr key={i.id}>
                <td data-label="Inizio">{fmtDate(i.createdAt)}</td>
                <td data-label="Con"><b>{i.other.name}</b><small> · utente HoloLens</small></td>
                <td data-label="Direzione">{i.direction === "incoming" ? "ricevuta" : "effettuata"}</td>
                <td data-label="Durata">{fmtDuration(i.durationSeconds)}</td>
                <td data-label="Esito">{outcome(i)}</td>
                <td data-label="Documenti">{i.documents || "–"}</td>
                <td data-label="Registrazione">
                  {i.recordingFiles?.length ? i.recordingFiles.map((f) => (
                    <a key={f.id} className="rec-link" href={`/api/recordings/${f.id}/file?access_token=${encodeURIComponent(token)}`} download={f.fileName} title={f.fileName}>
                      ⤓ Scarica ({(f.sizeBytes / 1_048_576).toFixed(1)} MB{f.durationSeconds ? ` · ${fmtDuration(f.durationSeconds)}` : ""})
                    </a>
                  )) : "–"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {data && data.total > PAGE && (
        <div className="row pager">
          <button className="ghost" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>‹ Precedenti</button>
          <span>pagina {page + 1} di {pages} · {data.total} chiamate</span>
          <button className="ghost" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)}>Successive ›</button>
        </div>
      )}
    </section>
  );
}
